import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import Checkbox from '../Checkbox';
import Text from '../Text';

import { ReactComponent as DropTickIcon } from 'icons/drop-tick.svg';
import { ReactComponent as PrevIcon } from 'icons/prev.svg';

import s from './Table.module.css';

const maxPage = (total, limit) => {
  const max = ~~(total / limit);
  const rest = total % limit;
  return rest > 0 ? max : max - 1;
};

const calcPages = (offset, total, limit) => {
  const pagerPositions = 6;
  const max = maxPage(total, limit);
  const current = ~~(offset / limit);

  let start = current < pagerPositions ? 0 : current - pagerPositions / 2;
  start = Math.min(start, max - pagerPositions + 1);
  start = Math.max(start, 0);

  let pages = [];
  let page;

  if (start > 0) {
    pages.push({
      page: 1,
      offset: 0,
    });
    pages.push({
      gap: true,
    });
  }

  for (page = start; page < start + pagerPositions; page++) {
    if (page > max) continue;
    pages.push({
      page: page + 1,
      offset: page * limit,
    });
  }

  if (page < max) {
    pages.push({
      gap: true,
    });
    pages.push({
      page: max + 1,
      offset: max * limit,
    });
  }

  return pages;
};

const globalSelectionState = ({ include, exclude }, total) => {
  if (include === 'all' && exclude.length === 0) return true;
  if (include.length === 0) return false;
  return 'indeterminate';
};

const onGlobalSelectionChange = ({ include, exclude }, total, onSelectionChange) => {
  const current = globalSelectionState({ include, exclude });

  onSelectionChange({ include: current === true ? [] : 'all', exclude: [] });
};

const isRowSelected = (rowId, { include, exclude }) => {
  if (exclude.includes(rowId)) return false;
  return include === 'all' || include.includes(rowId);
};

const onRowSelect = (rowId, total, selection, onSelectionChange) => {
  const selected = isRowSelected(rowId, selection);

  const { include, exclude } = selection;
  const newInclude =
    include === 'all' ? 'all' : selected ? include.filter(id => id !== rowId) : include.concat([rowId]);
  const newExclude = include !== 'all' ? [] : selected ? exclude.concat([rowId]) : exclude.filter(id => id !== rowId);

  onSelectionChange({ include: newInclude, exclude: newExclude });
};

const sortRows = (rows, orderBy, orderDirection, limit) => {
  if (!limit) return rows;
  const asc = orderDirection === 'ASC';
  const sorted = rows.sort((a, b) => {
    const aStr = (a[orderBy] || '').toString();
    const bStr = (b[orderBy] || '').toString();

    return aStr.localeCompare(bStr);
  });

  return asc ? sorted : sorted.reverse();
};

const Table = ({
  tableConfig,
  total,
  loading,
  selectable,
  selection,
  columns,
  rows,
  localSort,
  highlightText,
  onChange,
  onRowDoubleClick,
  onSelectionChange,
  renderActionComponent,
  highlightRow,
  rowId,
}) => {
  const allHighLightedRows = useRef([]);
  const [highlightRows, setHighlightRows] = useState([]);
  const [t] = useTranslation();
  const { offset, limit, orderBy, orderDirection } = tableConfig;

  // using for long tap
  const [timeDiff, setTimeDiff] = useState(null);

  useEffect(() => {
    if (highlightRow) {
      allHighLightedRows.current.push(highlightRow);
      setHighlightRows(allHighLightedRows.current);
      setTimeout(() => {
        setHighlightRows(allHighLightedRows.current.filter(r => rowId(r) !== rowId(highlightRow)));
        // magic number 800 = 500ms (animation) + 300ms reasonable gap
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightRow]);

  //convert number e.g. 5 into [0,1,2,3,4]
  const maxRows = Array.from(Array(limit || rows.length).keys());

  const sortedRows = localSort ? sortRows(rows, orderBy, orderDirection, maxRows.length) : rows;

  return (
    <div className={s.root}>
      {loading && <div className={s.loader} />}
      <div className={s.tableContainer}>
        <table className={cn(s.table, { [s.noColumns]: columns.length === 0 })}>
          <thead>
            <tr>
              {selectable && (
                <th className={s.checkbox}>
                  <Checkbox
                    disabled={total === 0}
                    checked={globalSelectionState(selection, total)}
                    onChange={() => onGlobalSelectionChange(selection, total, onSelectionChange)}
                  />
                </th>
              )}
              {columns.map(({ label, name, right, width }, index) => (
                <th
                  key={name}
                  className={cn({
                    [s.ordered]: orderBy === name,
                    [s.asc]: orderDirection === 'ASC',
                    [s.right]: right,
                  })}
                  style={{ minWidth: width ? `${width}px` : null }}
                  onClick={() =>
                    onChange({
                      ...tableConfig,
                      offset: 0,
                      orderBy: name,
                      orderDirection: orderBy === name && orderDirection === 'ASC' ? 'DESC' : 'ASC',
                    })
                  }
                >
                  {typeof label === 'function' ? label(t) : label}
                  {selectable && index === 0 && total > 0 && (
                    <span className={s.selectedInfo}>
                      {t('(выбрано {{selected, number}} из {{total, number}})', {
                        selected:
                          selection.include === 'all' ? total - selection.exclude.length : selection.include.length,
                        total,
                      })}
                    </span>
                  )}
                  <DropTickIcon className={s.tick} />
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {maxRows.map((_, rowIndex) => {
              const row = sortedRows[rowIndex];
              return (
                <tr
                  key={rowIndex}
                  className={cn({
                    [s.filled]: !!row && columns.length > 0,
                    [s.inactiveRow]: !!row && row.isInactive,
                    [s.highlight]: !!row && highlightRows.find(r => rowId(r) === rowId(row)),
                  })}
                  onClick={() => selectable && row && onRowSelect(rowId(row), total, selection, onSelectionChange)}
                  onDoubleClick={() => row && onRowDoubleClick(row)}
                  onTouchStart={() => setTimeDiff(new Date().getTime())}
                  onTouchEnd={() => (new Date().getTime() - timeDiff > 300 ? row && onRowDoubleClick(row) : null)}
                >
                  {selectable && (
                    <td className={s.checkbox}>
                      {row && (
                        <Checkbox
                          onClick={e => {
                            e.preventDefault();
                            onRowSelect(rowId(row), total, selection, onSelectionChange);
                          }}
                          checked={isRowSelected(rowId(row), selection)}
                        />
                      )}
                    </td>
                  )}
                  {columns.map(({ render, type, name, right, system }, index) => (
                    <td
                      className={cn({
                        [s.ordered]: orderBy === name,
                        [s.right]: right,
                        [s.multiline]: type === 'multiline',
                      })}
                      key={name}
                    >
                      {(!row || (!system && !row[name])) && <span className={s.empty} />}
                      {row && system && (render(row, t) || <span className={s.empty} />)}
                      {row && row[name] && <Text highlight={highlightText}>{render(row, t)}</Text>}
                    </td>
                  ))}
                  <td />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={s.footer}>
        {limit > 0 && total > limit && (
          <div className={s.pager}>
            <button onClick={() => onChange({ ...tableConfig, offset: 0 })}>{t('Начало')}</button>
            <button
              onClick={() => offset - limit >= 0 && onChange({ ...tableConfig, offset: offset - limit })}
              className={s.pagePrev}
            >
              <PrevIcon />
            </button>
            {calcPages(offset, total, limit).map(({ page, gap, offset: newOffset }, index) =>
              gap ? (
                <span key={index}>...</span>
              ) : (
                <button
                  key={index}
                  onClick={() => newOffset !== offset && onChange({ ...tableConfig, offset: newOffset })}
                  className={cn(s.pagerButton, { [s.pagerSelected]: newOffset === offset })}
                >
                  {page}
                </button>
              ),
            )}
            <button
              onClick={() => offset + limit < total && onChange({ ...tableConfig, offset: offset + limit })}
              className={s.pageNext}
            >
              <PrevIcon />
            </button>
            <button onClick={() => onChange({ ...tableConfig, offset: maxPage(total, limit) * limit })}>
              {t('Конец')}
            </button>
          </div>
        )}
        <span className={s.spacer} />
        {renderActionComponent && renderActionComponent()}
      </div>
    </div>
  );
};

Table.propTypes = {
  tableConfig: PropTypes.shape({
    offset: PropTypes.number,
    limit: PropTypes.number,
    orderBy: PropTypes.string.isRequired,
    orderDirection: PropTypes.oneOf(['DESC', 'ASC']).isRequired,
  }).isRequired,
  total: PropTypes.number,
  loading: PropTypes.bool,
  selectable: PropTypes.bool,
  selection: PropTypes.shape({
    include: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
    exclude: PropTypes.array.isRequired,
  }),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      render: PropTypes.func.isRequired,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  highlightText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func,
  onSelectionChange: PropTypes.func,
  rowId: PropTypes.func,
  localSort: PropTypes.bool,
  highlightRow: PropTypes.object,
};

Table.defaultProps = {
  total: 0,
  loading: false,
  selectable: false,
  localSort: false,
  selection: [],
  highlightText: undefined,
  onRowDoubleClick: () => {},
  onSelectionChange: () => {},
  rowId: ({ id }) => id,
};

export default React.memo(Table);
